/**
 * Super Admin — Content management:
 *   /superadmin/content/workouts    Workout Manager
 *   /superadmin/content/exercises   Exercise Database
 *   /superadmin/content/food        Food Database (+ CSV import)
 *   /superadmin/content/programmes  Programme Manager (week × day)
 *   /superadmin/content/community   Community Posts (Pin / Remove / Post-as-FitFrik)
 */
import { useEffect, useState, useCallback } from "react";
import { Plus, Search, Pin, PinOff, Trash2, Megaphone, Upload, Flag, Ban, Clock, ShieldAlert } from "lucide-react";
import { apiClient } from "../../services/api";
import { toast } from "sonner";
import { SectionHeader } from "./SuperadminApp";
import {
  Card, TableShell, Th, Td, Modal, PrimaryBtn, GhostBtn, DangerBtn, StatusBadge,
  inputStyle, fieldLabel, useDebouncedValue, EmptyState, FilterPills, relTime,
} from "./_atoms";

const MODULE_OPTS = [
  { value: "gym", label: "Gym" }, { value: "home", label: "Home" },
  { value: "yoga", label: "Yoga" }, { value: "zumba", label: "Zumba" },
];
const DIFF_OPTS = [
  { value: "beginner", label: "Beginner" }, { value: "intermediate", label: "Intermediate" }, { value: "advanced", label: "Advanced" },
];

// ───────────────────────────────────────────────────────────────────
// Workouts
// ───────────────────────────────────────────────────────────────────
export function WorkoutManager() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState(""); const dq = useDebouncedValue(q, 300);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => {
    apiClient.get("/superadmin/content/workouts", { params: { q: dq || undefined } })
      .then((r) => setRows(r.data?.workouts || []));
  }, [dq]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="content-workouts">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
        <SectionHeader title="Workout Manager" onRefresh={load} />
        <PrimaryBtn onClick={() => setOpen({})} data-testid="new-workout"><Plus size={14} /> New Workout</PrimaryBtn>
      </div>
      <Card>
        <div style={{ position: "relative" }}>
          <Search size={14} style={{ position: "absolute", top: 15, left: 14, color: "var(--text-tertiary, #8A8884)" }} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name…" style={{ ...inputStyle, paddingLeft: 36 }} data-testid="workout-search" />
        </div>
      </Card>
      {rows.length === 0 ? <EmptyState title="No workouts" /> : (
        <TableShell>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr><Th>Name</Th><Th>Module</Th><Th>Difficulty</Th><Th>Duration</Th><Th>Started</Th><Th>Completion</Th><Th>Status</Th><Th></Th></tr></thead>
            <tbody>
              {rows.map((w) => (
                <tr key={w.plan_id || w.workout_id}>
                  <Td><span style={{ fontWeight: 600, color: "var(--text-primary, #0C0C0B)" }}>{w.plan_name || w.name}</span></Td>
                  <Td>{w.module}</Td><Td>{w.difficulty || "—"}</Td>
                  <Td>{w.duration_minutes ? `${w.duration_minutes}m` : "—"}</Td>
                  <Td>{w.started ?? 0}</Td>
                  <Td>{w.completion_rate}%</Td>
                  <Td><StatusBadge value={w.status || "active"} /></Td>
                  <Td>
                    {w.status !== "draft" && (
                      <GhostBtn
                        onClick={async () => {
                          await apiClient.post(`/superadmin/content/workouts/${w.plan_id || w.workout_id}/deactivate`);
                          toast.success("Deactivated");
                          load();
                        }}
                        style={{ height: 30, padding: "0 12px", fontSize: 12 }}
                        data-testid={`deactivate-${w.plan_id}`}
                      >
                        Deactivate
                      </GhostBtn>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableShell>
      )}
      {open && <WorkoutForm onClose={() => setOpen(null)} onSaved={() => { setOpen(null); load(); }} />}
    </div>
  );
}

function WorkoutForm({ onClose, onSaved }) {
  const [f, setF] = useState({
    name: "", module: "gym", difficulty: "beginner", duration_minutes: 30,
    description: "", thumbnail_url: "", status: "active", exercises: [],
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.name) return toast.error("Name required");
    setBusy(true);
    try {
      await apiClient.post("/superadmin/content/workouts/create", f);
      toast.success("Workout created");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title="New Workout">
      <div style={{ display: "grid", gap: 12 }}>
        <div><label style={fieldLabel}>Name</label><input style={inputStyle} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="wk-name" /></div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div><label style={fieldLabel}>Module</label>
            <select style={inputStyle} value={f.module} onChange={(e) => setF({ ...f, module: e.target.value })}>
              {MODULE_OPTS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
          <div><label style={fieldLabel}>Difficulty</label>
            <select style={inputStyle} value={f.difficulty} onChange={(e) => setF({ ...f, difficulty: e.target.value })}>
              {DIFF_OPTS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
        </div>
        <div><label style={fieldLabel}>Duration (min)</label><input type="number" style={inputStyle} value={f.duration_minutes} onChange={(e) => setF({ ...f, duration_minutes: parseInt(e.target.value, 10) || 0 })} /></div>
        <div><label style={fieldLabel}>Description</label><textarea style={{ ...inputStyle, height: 80, padding: "10px 14px" }} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></div>
        <div><label style={fieldLabel}>Thumbnail URL</label><input style={inputStyle} value={f.thumbnail_url} onChange={(e) => setF({ ...f, thumbnail_url: e.target.value })} /></div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <GhostBtn onClick={onClose}>Cancel</GhostBtn>
          <PrimaryBtn onClick={submit} disabled={busy} data-testid="wk-save">{busy ? "Saving…" : "Save"}</PrimaryBtn>
        </div>
      </div>
    </Modal>
  );
}

// ───────────────────────────────────────────────────────────────────
// Exercises
// ───────────────────────────────────────────────────────────────────
export function ExerciseDatabase() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState(""); const dq = useDebouncedValue(q, 300);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => {
    apiClient.get("/superadmin/content/exercises", { params: { q: dq || undefined } })
      .then((r) => setRows(r.data?.exercises || []));
  }, [dq]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-4" data-testid="content-exercises">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <SectionHeader title="Exercise Database" onRefresh={load} />
        <PrimaryBtn onClick={() => setOpen({})} data-testid="new-exercise"><Plus size={14} /> New Exercise</PrimaryBtn>
      </div>
      <Card>
        <div style={{ position: "relative" }}>
          <Search size={14} style={{ position: "absolute", top: 15, left: 14, color: "var(--text-tertiary, #8A8884)" }} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search exercise name…" style={{ ...inputStyle, paddingLeft: 36 }} />
        </div>
      </Card>
      {rows.length === 0 ? <EmptyState title="No exercises" /> : (
        <TableShell>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr><Th>Name</Th><Th>Category</Th><Th>Module</Th><Th>Equipment</Th><Th>Primary muscles</Th><Th>AI Detect</Th></tr></thead>
            <tbody>
              {/* asArr: defensively coerce string|array|null → array for legacy exercise docs */}

              {rows.map((e) => (
                <tr key={e.exercise_id} onClick={() => setOpen(e)} style={{ cursor: "pointer" }} data-testid={`ex-row-${e.exercise_id}`}>
                  <Td><strong>{e.name}</strong></Td>
                  <Td>{e.category || "—"}</Td>
                  <Td>{e.module}</Td>
                  <Td style={{ fontSize: 12 }}>{(Array.isArray(e.equipment) ? e.equipment : (e.equipment ? [e.equipment] : [])).join(", ") || "—"}</Td>
                  <Td style={{ fontSize: 12 }}>{(Array.isArray(e.primary_muscles) ? e.primary_muscles : (e.primary_muscles ? [e.primary_muscles] : [])).join(", ") || "—"}</Td>
                  <Td>{e.ai_trainer_detection ? "✓" : "—"}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableShell>
      )}
      {open && <ExerciseForm initial={open} onClose={() => setOpen(null)} onSaved={() => { setOpen(null); load(); }} />}
    </div>
  );
}

function ExerciseForm({ initial, onClose, onSaved }) {
  const editing = !!initial.exercise_id;
  const [f, setF] = useState({
    name: initial.name || "", category: initial.category || "", module: initial.module || "all",
    equipment: (Array.isArray(initial.equipment) ? initial.equipment : (initial.equipment ? [initial.equipment] : [])).join(", "),
    primary_muscles: (Array.isArray(initial.primary_muscles) ? initial.primary_muscles : (initial.primary_muscles ? [initial.primary_muscles] : [])).join(", "),
    secondary_muscles: (Array.isArray(initial.secondary_muscles) ? initial.secondary_muscles : (initial.secondary_muscles ? [initial.secondary_muscles] : [])).join(", "),
    instructions: initial.instructions || "", video_url: initial.video_url || "",
    ai_trainer_detection: !!initial.ai_trainer_detection,
    default_sets: initial.default_sets || 3, default_reps: initial.default_reps || 10,
    default_rest_sec: initial.default_rest_sec || 60, active: initial.active !== false,
  });
  const submit = async () => {
    const body = {
      ...f,
      equipment: f.equipment.split(",").map((s) => s.trim()).filter(Boolean),
      primary_muscles: f.primary_muscles.split(",").map((s) => s.trim()).filter(Boolean),
      secondary_muscles: f.secondary_muscles.split(",").map((s) => s.trim()).filter(Boolean),
      default_sets: parseInt(f.default_sets, 10) || 3,
      default_reps: parseInt(f.default_reps, 10) || 10,
      default_rest_sec: parseInt(f.default_rest_sec, 10) || 60,
    };
    try {
      if (editing) await apiClient.put(`/superadmin/content/exercises/${initial.exercise_id}`, body);
      else await apiClient.post("/superadmin/content/exercises/create", body);
      toast.success("Saved"); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <Modal open onClose={onClose} title={editing ? "Edit Exercise" : "New Exercise"} maxWidth={620}>
      <div style={{ display: "grid", gap: 10 }}>
        <div><label style={fieldLabel}>Name</label><input style={inputStyle} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="ex-name" /></div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div><label style={fieldLabel}>Category</label><input style={inputStyle} value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} placeholder="strength, cardio…" /></div>
          <div><label style={fieldLabel}>Module</label>
            <select style={inputStyle} value={f.module} onChange={(e) => setF({ ...f, module: e.target.value })}>
              <option value="all">All</option>
              {MODULE_OPTS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
        </div>
        <div><label style={fieldLabel}>Equipment (comma separated)</label><input style={inputStyle} value={f.equipment} onChange={(e) => setF({ ...f, equipment: e.target.value })} /></div>
        <div><label style={fieldLabel}>Primary muscles</label><input style={inputStyle} value={f.primary_muscles} onChange={(e) => setF({ ...f, primary_muscles: e.target.value })} /></div>
        <div><label style={fieldLabel}>Secondary muscles</label><input style={inputStyle} value={f.secondary_muscles} onChange={(e) => setF({ ...f, secondary_muscles: e.target.value })} /></div>
        <div><label style={fieldLabel}>Video URL</label><input style={inputStyle} value={f.video_url} onChange={(e) => setF({ ...f, video_url: e.target.value })} /></div>
        <div><label style={fieldLabel}>Instructions</label><textarea style={{ ...inputStyle, height: 80, padding: "10px 14px" }} value={f.instructions} onChange={(e) => setF({ ...f, instructions: e.target.value })} /></div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
          <div><label style={fieldLabel}>Default sets</label><input type="number" style={inputStyle} value={f.default_sets} onChange={(e) => setF({ ...f, default_sets: e.target.value })} /></div>
          <div><label style={fieldLabel}>Default reps</label><input type="number" style={inputStyle} value={f.default_reps} onChange={(e) => setF({ ...f, default_reps: e.target.value })} /></div>
          <div><label style={fieldLabel}>Rest sec</label><input type="number" style={inputStyle} value={f.default_rest_sec} onChange={(e) => setF({ ...f, default_rest_sec: e.target.value })} /></div>
        </div>
        <label style={{ display: "flex", gap: 8, alignItems: "center" }}><input type="checkbox" checked={f.ai_trainer_detection} onChange={(e) => setF({ ...f, ai_trainer_detection: e.target.checked })} /> AI Trainer detection supported</label>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
          <GhostBtn onClick={onClose}>Cancel</GhostBtn>
          <PrimaryBtn onClick={submit} data-testid="ex-save">Save</PrimaryBtn>
        </div>
      </div>
    </Modal>
  );
}

// ───────────────────────────────────────────────────────────────────
// Food Database
// ───────────────────────────────────────────────────────────────────
export function FoodDatabase() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState(""); const dq = useDebouncedValue(q, 300);
  const [open, setOpen] = useState(null);
  const [csvOpen, setCsvOpen] = useState(false);
  const [misses, setMisses] = useState([]);
  const [showMisses, setShowMisses] = useState(false);
  const load = useCallback(() => {
    apiClient.get("/superadmin/content/food", { params: { q: dq || undefined } })
      .then((r) => setRows(r.data?.items || []));
  }, [dq]);
  const loadMisses = useCallback(() => {
    apiClient.get("/superadmin/content/food/search-misses?limit=100")
      .then((r) => setMisses(r.data?.items || []))
      .catch(() => setMisses([]));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (showMisses) loadMisses(); }, [showMisses, loadMisses]);
  return (
    <div className="space-y-4" data-testid="content-food">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <SectionHeader title="Food Database" onRefresh={load} />
        <div style={{ display: "flex", gap: 8 }}>
          <GhostBtn
            onClick={() => setShowMisses((v) => !v)}
            data-testid="food-toggle-misses"
          >
            <Search size={14} /> {showMisses ? "Hide" : "View"} search misses
          </GhostBtn>
          <GhostBtn onClick={() => setCsvOpen(true)} data-testid="food-import-csv"><Upload size={14} /> Import CSV</GhostBtn>
          <PrimaryBtn onClick={() => setOpen({})} data-testid="new-food"><Plus size={14} /> New Item</PrimaryBtn>
        </div>
      </div>
      {showMisses && (
        <Card>
          <div style={{ padding: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>
              Top search misses ({misses.length})
            </div>
            <div style={{ fontSize: 11, color: "var(--text-tertiary, #8A8884)", marginBottom: 10 }}>
              Real queries users typed and could not find. Ranked by count. Use this
              list to prioritise DB additions. User-supplied macros are shown for
              context — <strong>do not auto-promote</strong>; verify before adding.
            </div>
            {misses.length === 0 ? (
              <div style={{ padding: 12, color: "var(--text-tertiary, #8A8884)", fontSize: 12 }}>
                No misses recorded yet.
              </div>
            ) : (
              <TableShell>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <Th>Query</Th><Th>Count</Th><Th>Last searched</Th><Th>User entries (macros)</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {misses.map((m) => (
                      <tr key={m.query} data-testid={`miss-row-${m.query}`}>
                        <Td><strong>{m.query}</strong></Td>
                        <Td>{m.count}</Td>
                        <Td>{(m.last_searched_at || "").slice(0, 10) || "—"}</Td>
                        <Td>
                          {(m.user_entries || []).length === 0 ? (
                            <span style={{ color: "var(--text-tertiary, #8A8884)" }}>—</span>
                          ) : (
                            <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                              {(m.user_entries || []).slice(-3).map((e, i) => (
                                <span key={i} style={{ fontSize: 11 }}>
                                  {e.name} — {Math.round(e.calories)} kcal
                                  (P{e.protein_g} C{e.carbs_g} F{e.fat_g})
                                </span>
                              ))}
                            </div>
                          )}
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableShell>
            )}
          </div>
        </Card>
      )}
      <Card>
        <div style={{ position: "relative" }}>
          <Search size={14} style={{ position: "absolute", top: 15, left: 14, color: "var(--text-tertiary, #8A8884)" }} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search food, indian name…" style={{ ...inputStyle, paddingLeft: 36 }} />
        </div>
      </Card>
      {rows.length === 0 ? <EmptyState title="No food entries" /> : (
        <TableShell>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr><Th>Name</Th><Th>Indian name</Th><Th>Cal</Th><Th>P</Th><Th>C</Th><Th>F</Th><Th>Source</Th></tr></thead>
            <tbody>
              {rows.map((it) => (
                <tr key={it.food_id} onClick={() => setOpen(it)} style={{ cursor: "pointer" }} data-testid={`food-row-${it.food_id}`}>
                  <Td><strong>{it.name}</strong></Td>
                  <Td>{it.indian_name || "—"}</Td>
                  <Td>{it.calories}</Td>
                  <Td>{it.protein_g}g</Td>
                  <Td>{it.carbs_g}g</Td>
                  <Td>{it.fats_g}g</Td>
                  <Td>{it.source || "—"}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableShell>
      )}
      {open && <FoodForm initial={open} onClose={() => setOpen(null)} onSaved={() => { setOpen(null); load(); }} />}
      {csvOpen && <CsvImportModal onClose={() => setCsvOpen(false)} onSaved={() => { setCsvOpen(false); load(); }} />}
    </div>
  );
}

function FoodForm({ initial, onClose, onSaved }) {
  const editing = !!initial.food_id;
  const [f, setF] = useState({
    name: initial.name || "", indian_name: initial.indian_name || "",
    portion_description: initial.portion_description || "", portion_grams: initial.portion_grams || 0,
    calories: initial.calories || 0, protein_g: initial.protein_g || 0, carbs_g: initial.carbs_g || 0,
    fats_g: initial.fats_g || 0, fibre_g: initial.fibre_g || 0, sugar_g: initial.sugar_g || 0,
    sodium_mg: initial.sodium_mg || 0, glycemic_index: initial.glycemic_index || 0,
    source: initial.source || "indian_db", active: initial.active !== false,
  });
  const submit = async () => {
    const body = {
      ...f,
      portion_grams: parseFloat(f.portion_grams) || 0,
      calories: parseInt(f.calories, 10) || 0,
      protein_g: parseFloat(f.protein_g) || 0, carbs_g: parseFloat(f.carbs_g) || 0,
      fats_g: parseFloat(f.fats_g) || 0, fibre_g: parseFloat(f.fibre_g) || 0,
      sugar_g: parseFloat(f.sugar_g) || 0, sodium_mg: parseInt(f.sodium_mg, 10) || 0,
      glycemic_index: parseInt(f.glycemic_index, 10) || null,
    };
    try {
      if (editing) await apiClient.put(`/superadmin/content/food/${initial.food_id}`, body);
      else await apiClient.post("/superadmin/content/food/create", body);
      toast.success("Saved"); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <Modal open onClose={onClose} title={editing ? "Edit Food" : "New Food"} maxWidth={620}>
      <div style={{ display: "grid", gap: 10 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div><label style={fieldLabel}>Name</label><input style={inputStyle} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="food-name" /></div>
          <div><label style={fieldLabel}>Indian name</label><input style={inputStyle} value={f.indian_name} onChange={(e) => setF({ ...f, indian_name: e.target.value })} /></div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div><label style={fieldLabel}>Portion description</label><input style={inputStyle} value={f.portion_description} onChange={(e) => setF({ ...f, portion_description: e.target.value })} placeholder="1 katori (100g)" /></div>
          <div><label style={fieldLabel}>Portion grams</label><input type="number" style={inputStyle} value={f.portion_grams} onChange={(e) => setF({ ...f, portion_grams: e.target.value })} /></div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
          <div><label style={fieldLabel}>Calories</label><input type="number" style={inputStyle} value={f.calories} onChange={(e) => setF({ ...f, calories: e.target.value })} /></div>
          <div><label style={fieldLabel}>Protein g</label><input type="number" style={inputStyle} value={f.protein_g} onChange={(e) => setF({ ...f, protein_g: e.target.value })} /></div>
          <div><label style={fieldLabel}>Carbs g</label><input type="number" style={inputStyle} value={f.carbs_g} onChange={(e) => setF({ ...f, carbs_g: e.target.value })} /></div>
          <div><label style={fieldLabel}>Fats g</label><input type="number" style={inputStyle} value={f.fats_g} onChange={(e) => setF({ ...f, fats_g: e.target.value })} /></div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
          <div><label style={fieldLabel}>Fibre g</label><input type="number" style={inputStyle} value={f.fibre_g} onChange={(e) => setF({ ...f, fibre_g: e.target.value })} /></div>
          <div><label style={fieldLabel}>Sugar g</label><input type="number" style={inputStyle} value={f.sugar_g} onChange={(e) => setF({ ...f, sugar_g: e.target.value })} /></div>
          <div><label style={fieldLabel}>Sodium mg</label><input type="number" style={inputStyle} value={f.sodium_mg} onChange={(e) => setF({ ...f, sodium_mg: e.target.value })} /></div>
          <div><label style={fieldLabel}>GI</label><input type="number" style={inputStyle} value={f.glycemic_index} onChange={(e) => setF({ ...f, glycemic_index: e.target.value })} /></div>
        </div>
        <div><label style={fieldLabel}>Source</label><input style={inputStyle} value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })} /></div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <GhostBtn onClick={onClose}>Cancel</GhostBtn>
          <PrimaryBtn onClick={submit} data-testid="food-save">Save</PrimaryBtn>
        </div>
      </div>
    </Modal>
  );
}

function CsvImportModal({ onClose, onSaved }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const lines = text.split("\n").map((l) => l.trim()).filter(Boolean);
    if (lines.length < 2) return toast.error("Need header + at least 1 row");
    const header = lines[0].split(",").map((h) => h.trim());
    const rows = lines.slice(1).map((l) => {
      const parts = l.split(",").map((s) => s.trim());
      const o = {};
      header.forEach((h, i) => { o[h] = parts[i] ?? ""; });
      return {
        name: o.name, indian_name: o.indian_name || null,
        portion_description: o.portion_description || null,
        portion_grams: parseFloat(o.portion_grams) || 0,
        calories: parseInt(o.calories, 10) || 0,
        protein_g: parseFloat(o.protein_g) || 0,
        carbs_g: parseFloat(o.carbs_g) || 0,
        fats_g: parseFloat(o.fats_g) || 0,
        fibre_g: parseFloat(o.fibre_g) || 0,
        sugar_g: parseFloat(o.sugar_g) || 0,
        sodium_mg: parseInt(o.sodium_mg, 10) || 0,
        glycemic_index: parseInt(o.glycemic_index, 10) || null,
        source: o.source || "csv",
      };
    });
    setBusy(true);
    try {
      const { data } = await apiClient.post("/superadmin/content/food/import-csv", { rows });
      toast.success(`Inserted ${data.inserted}, skipped ${data.skipped}`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title="Import Food CSV" maxWidth={620}>
      <p style={{ fontSize: 12, color: "var(--text-tertiary, #8A8884)" }}>
        First line = header. Required: <code>name</code>. Optional: indian_name, portion_description, portion_grams, calories, protein_g, carbs_g, fats_g, fibre_g, sugar_g, sodium_mg, glycemic_index, source. Duplicate names are skipped.
      </p>
      <textarea
        value={text} onChange={(e) => setText(e.target.value)}
        style={{ ...inputStyle, height: 220, padding: "10px 14px", fontFamily: "monospace", fontSize: 12, marginTop: 12 }}
        placeholder="name,indian_name,calories,protein_g,carbs_g,fats_g..."
        data-testid="csv-textarea"
      />
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
        <GhostBtn onClick={onClose}>Cancel</GhostBtn>
        <PrimaryBtn onClick={submit} disabled={busy} data-testid="csv-import">{busy ? "Importing…" : "Import"}</PrimaryBtn>
      </div>
    </Modal>
  );
}

// ───────────────────────────────────────────────────────────────────
// Programmes
// ───────────────────────────────────────────────────────────────────
export function ProgrammeManager() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => {
    apiClient.get("/superadmin/content/programmes").then((r) => setRows(r.data?.programmes || []));
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-4" data-testid="content-programmes">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <SectionHeader title="Programme Manager" onRefresh={load} />
        <PrimaryBtn onClick={() => setOpen({})} data-testid="new-programme"><Plus size={14} /> New Programme</PrimaryBtn>
      </div>
      {rows.length === 0 ? <EmptyState title="No programmes" sub="Create a multi-week programme to bundle workouts." /> : (
        <TableShell>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr><Th>Name</Th><Th>Module</Th><Th>Difficulty</Th><Th>Weeks</Th><Th>Status</Th></tr></thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.programme_id} onClick={() => setOpen(p)} style={{ cursor: "pointer" }} data-testid={`prog-row-${p.programme_id}`}>
                  <Td><strong>{p.name}</strong></Td>
                  <Td>{p.module}</Td>
                  <Td>{p.difficulty}</Td>
                  <Td>{p.duration_weeks}</Td>
                  <Td><StatusBadge value={p.status} /></Td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableShell>
      )}
      {open && <ProgrammeForm initial={open} onClose={() => setOpen(null)} onSaved={() => { setOpen(null); load(); }} />}
    </div>
  );
}

function ProgrammeForm({ initial, onClose, onSaved }) {
  const editing = !!initial.programme_id;
  const [f, setF] = useState({
    name: initial.name || "", module: initial.module || "gym", difficulty: initial.difficulty || "beginner",
    duration_weeks: initial.duration_weeks || 4, description: initial.description || "",
    cover_image_url: initial.cover_image_url || "",
    weekly_schedule: initial.weekly_schedule || Array.from({ length: 4 }, () => Array(7).fill(null)),
    status: initial.status || "active",
  });
  const updateWeeks = (n) => {
    const cur = f.weekly_schedule || [];
    const nf = Array.from({ length: n }, (_, i) => cur[i] || Array(7).fill(null));
    setF({ ...f, duration_weeks: n, weekly_schedule: nf });
  };
  const updateCell = (w, d, v) => {
    const ws = f.weekly_schedule.map((row) => [...row]);
    ws[w][d] = v || null;
    setF({ ...f, weekly_schedule: ws });
  };
  const submit = async () => {
    if (!f.name) return toast.error("Name required");
    try {
      if (editing) await apiClient.put(`/superadmin/content/programmes/${initial.programme_id}`, f);
      else await apiClient.post("/superadmin/content/programmes/create", f);
      toast.success("Saved"); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  return (
    <Modal open onClose={onClose} title={editing ? "Edit Programme" : "New Programme"} maxWidth={760}>
      <div style={{ display: "grid", gap: 10 }}>
        <div><label style={fieldLabel}>Name</label><input style={inputStyle} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="prog-name" /></div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
          <div><label style={fieldLabel}>Module</label>
            <select style={inputStyle} value={f.module} onChange={(e) => setF({ ...f, module: e.target.value })}>
              {MODULE_OPTS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
          <div><label style={fieldLabel}>Difficulty</label>
            <select style={inputStyle} value={f.difficulty} onChange={(e) => setF({ ...f, difficulty: e.target.value })}>
              {DIFF_OPTS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
          <div><label style={fieldLabel}>Duration (weeks)</label>
            <input type="number" min="1" max="16" style={inputStyle} value={f.duration_weeks}
              onChange={(e) => updateWeeks(Math.max(1, Math.min(16, parseInt(e.target.value, 10) || 1)))} />
          </div>
        </div>
        <div><label style={fieldLabel}>Description</label><textarea style={{ ...inputStyle, height: 60, padding: "10px 14px" }} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></div>
        <div><label style={fieldLabel}>Cover image URL</label><input style={inputStyle} value={f.cover_image_url} onChange={(e) => setF({ ...f, cover_image_url: e.target.value })} /></div>
        <label style={fieldLabel}>Weekly Schedule (paste plan_id or leave blank for rest)</label>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <thead><tr><th></th>{days.map((d) => <th key={d} style={{ padding: 4, textTransform: "uppercase", color: "var(--text-tertiary, #8A8884)" }}>{d}</th>)}</tr></thead>
            <tbody>
              {(f.weekly_schedule || []).map((week, w) => (
                <tr key={w}>
                  <td style={{ padding: 4, fontWeight: 700 }}>W{w + 1}</td>
                  {days.map((_, d) => (
                    <td key={d} style={{ padding: 2 }}>
                      <input
                        value={week[d] || ""} onChange={(e) => updateCell(w, d, e.target.value)}
                        placeholder="—"
                        style={{ ...inputStyle, height: 32, fontSize: 11, padding: "0 6px" }}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
          <GhostBtn onClick={onClose}>Cancel</GhostBtn>
          <PrimaryBtn onClick={submit} data-testid="prog-save">Save</PrimaryBtn>
        </div>
      </div>
    </Modal>
  );
}

// ───────────────────────────────────────────────────────────────────
// Community Posts
// ───────────────────────────────────────────────────────────────────
export function CommunityModeration() {
  const [rows, setRows] = useState([]);
  const [reportedOnly, setReportedOnly] = useState(false);
  const [openPost, setOpenPost] = useState(false);
  const load = useCallback(() => {
    apiClient.get("/superadmin/content/posts", { params: { reported: reportedOnly } })
      .then((r) => setRows(r.data?.posts || []));
  }, [reportedOnly]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="content-community">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <SectionHeader title="Community Posts" onRefresh={load} />
        <PrimaryBtn onClick={() => setOpenPost(true)} data-testid="post-as-fitfrik"><Megaphone size={14} /> Post as FitFrik</PrimaryBtn>
      </div>
      <Card>
        <FilterPills
          value={reportedOnly ? "reported" : "all"}
          onChange={(v) => setReportedOnly(v === "reported")}
          options={[{ value: "all", label: "All posts" }, { value: "reported", label: "Reported only" }]}
          testid="post-filter"
        />
      </Card>
      {rows.length === 0 ? <EmptyState title="No posts" /> : (
        <div style={{ display: "grid", gap: 10 }}>
          {rows.map((p) => (
            <Card key={p.post_id} data-testid={`post-row-${p.post_id}`}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4 }}>
                    <strong>{p.author_name || p.user_id}</strong>
                    {p.is_official && <span style={{ background: "var(--brand-gold, #F5C518)", color: "#0C0C0B", padding: "2px 8px", borderRadius: 100, fontSize: 10, fontWeight: 700 }}>OFFICIAL</span>}
                    {p.is_pinned && <span style={{ background: "rgba(42,40,37,0.10)", color: "var(--text-secondary, #4A4845)", padding: "2px 8px", borderRadius: 100, fontSize: 10, fontWeight: 700 }}>PINNED</span>}
                    {p.removed_at && <StatusBadge value="removed" />}
                  </div>
                  <div style={{ color: "var(--text-primary, #0C0C0B)", fontSize: 14, marginBottom: 6 }}>{p.text}</div>
                  <div style={{ fontSize: 11, color: "var(--text-tertiary, #8A8884)" }}>
                    {p.hub} · {(p.likes || []).length} likes · {p.reports_count ? `${p.reports_count} reports` : "0 reports"} · {relTime(p.created_at)}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  {!p.is_pinned ? (
                    <GhostBtn
                      onClick={async () => { await apiClient.post(`/superadmin/content/posts/${p.post_id}/pin`); toast.success("Pinned"); load(); }}
                      style={{ height: 32, padding: "0 10px", fontSize: 11 }} data-testid={`pin-${p.post_id}`}
                    ><Pin size={12} /> Pin</GhostBtn>
                  ) : (
                    <GhostBtn
                      onClick={async () => { await apiClient.post(`/superadmin/content/posts/${p.post_id}/unpin`); toast.success("Unpinned"); load(); }}
                      style={{ height: 32, padding: "0 10px", fontSize: 11 }}
                    ><PinOff size={12} /> Unpin</GhostBtn>
                  )}
                  {!p.removed_at && (
                    <DangerBtn
                      onClick={async () => { await apiClient.post(`/superadmin/content/posts/${p.post_id}/remove`); toast.success("Removed"); load(); }}
                      style={{ height: 32, padding: "0 10px", fontSize: 11 }} data-testid={`remove-${p.post_id}`}
                    ><Trash2 size={12} /> Remove</DangerBtn>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
      {openPost && <PostAsFitFrikModal onClose={() => setOpenPost(false)} onSaved={() => { setOpenPost(false); load(); }} />}
    </div>
  );
}

function PostAsFitFrikModal({ onClose, onSaved }) {
  const [f, setF] = useState({ text: "", hub: "all", photo_url: "" });
  const submit = async () => {
    if (!f.text) return toast.error("Text required");
    try {
      await apiClient.post("/superadmin/content/posts/fitfrik", f);
      toast.success("Posted");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <Modal open onClose={onClose} title="Post as FitFrik (Official)">
      <div style={{ display: "grid", gap: 10 }}>
        <div><label style={fieldLabel}>Text (max 280)</label><textarea maxLength={280} value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} style={{ ...inputStyle, height: 100, padding: "10px 14px" }} data-testid="ff-post-text" /></div>
        <div><label style={fieldLabel}>Hub</label><input style={inputStyle} value={f.hub} onChange={(e) => setF({ ...f, hub: e.target.value })} placeholder="all / mumbai / delhi…" /></div>
        <div><label style={fieldLabel}>Photo URL (optional)</label><input style={inputStyle} value={f.photo_url} onChange={(e) => setF({ ...f, photo_url: e.target.value })} /></div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <GhostBtn onClick={onClose}>Cancel</GhostBtn>
          <PrimaryBtn onClick={submit} data-testid="ff-post-submit">Post</PrimaryBtn>
        </div>
      </div>
    </Modal>
  );
}


// ───────────────────────────────────────────────────────────────────
// Content Reports queue (Session 2)
// ───────────────────────────────────────────────────────────────────
export function ContentReports() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/superadmin/community/reports", { params: { filter } });
      setRows(data?.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.error || "Failed to load");
    }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const act = async (row, action) => {
    setBusy(`${row.content_id}:${action}`);
    try {
      await apiClient.post("/superadmin/community/reports/action", {
        content_type: row.content_type,
        content_id: row.content_id,
        action,
        author_id: row.content_owner_id,
      });
      toast.success(action === "dismiss" ? "Dismissed" : "Action taken");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.error || "Failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="content-reports">
      <SectionHeader title="Content Reports" onRefresh={load} />
      <Card>
        <FilterPills
          value={filter}
          onChange={setFilter}
          options={[
            { value: "all", label: "All" },
            { value: "pending", label: "Pending" },
            { value: "urgent", label: "Urgent" },
            { value: "actioned", label: "Actioned" },
            { value: "dismissed", label: "Dismissed" },
          ]}
          testid="reports-filter"
        />
      </Card>
      {rows.length === 0 ? <EmptyState title="No reports" /> : (
        <div style={{ display: "grid", gap: 10 }}>
          {rows.map((r) => (
            <Card key={`${r.content_type}:${r.content_id}`} data-testid={`report-row-${r.content_id}`}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 260 }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4, flexWrap: "wrap" }}>
                    <span style={{ background: "#0C0C0B", color: "#F4F3F1", padding: "2px 8px", borderRadius: 100, fontSize: 10, fontWeight: 700, textTransform: "uppercase" }}>{r.content_type}</span>
                    {r.urgent && (
                      <span style={{ background: "#FEE2E2", color: "#7F1D1D", padding: "2px 8px", borderRadius: 100, fontSize: 10, fontWeight: 700, textTransform: "uppercase", display: "inline-flex", alignItems: "center", gap: 4 }}>
                        <ShieldAlert size={11} /> Urgent
                      </span>
                    )}
                    {r.content_status && r.content_status !== "active" && <StatusBadge value={r.content_status} />}
                  </div>
                  <div style={{ color: "var(--text-primary, #0C0C0B)", fontSize: 14, marginBottom: 6, whiteSpace: "pre-wrap" }}>{r.preview || "(no preview)"}</div>
                  <div style={{ fontSize: 11, color: "var(--text-tertiary, #8A8884)" }}>
                    {r.report_count} reports · {r.reporter_count} distinct users · reasons: {(r.reasons || []).join(", ")}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--text-tertiary, #8A8884)", marginTop: 2 }}>
                    Author: {r.content_owner_id || "unknown"} · First: {relTime(r.first_report_at)}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  <DangerBtn
                    disabled={busy === `${r.content_id}:remove`}
                    onClick={() => act(r, "remove")}
                    style={{ height: 32, padding: "0 10px", fontSize: 11 }}
                    data-testid={`report-remove-${r.content_id}`}
                  ><Trash2 size={12} /> Remove</DangerBtn>
                  <GhostBtn
                    disabled={busy === `${r.content_id}:dismiss`}
                    onClick={() => act(r, "dismiss")}
                    style={{ height: 32, padding: "0 10px", fontSize: 11 }}
                    data-testid={`report-dismiss-${r.content_id}`}
                  >Dismiss</GhostBtn>
                  <GhostBtn
                    disabled={busy === `${r.content_id}:warn`}
                    onClick={() => act(r, "warn")}
                    style={{ height: 32, padding: "0 10px", fontSize: 11 }}
                    data-testid={`report-warn-${r.content_id}`}
                  ><Flag size={12} /> Warn</GhostBtn>
                  <GhostBtn
                    disabled={busy === `${r.content_id}:restrict7`}
                    onClick={() => act(r, "restrict7")}
                    style={{ height: 32, padding: "0 10px", fontSize: 11 }}
                    data-testid={`report-restrict7-${r.content_id}`}
                  ><Clock size={12} /> Restrict 7d</GhostBtn>
                  <DangerBtn
                    disabled={busy === `${r.content_id}:ban`}
                    onClick={() => act(r, "ban")}
                    style={{ height: 32, padding: "0 10px", fontSize: 11 }}
                    data-testid={`report-ban-${r.content_id}`}
                  ><Ban size={12} /> Ban</DangerBtn>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
